/* eslint-disable sonarjs/cognitive-complexity, complexity, @next/next/no-img-element, react/no-array-index-key, prefer-const */
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cookies from 'react-cookies';
import { useDispatch } from 'react-redux';
import { useSWRInfinite } from 'swr';
import { TRACKING } from '../../../config/constants/action-types';
import { ORDER } from '../../../config/constants/common';
import { VIP_BG } from '../../../config/constants/images';
import { VIP_QUALIFIERS } from '../../../config/constants/page-types';
import COOKIES from '../../../config/cookies/cookies';
import { DESKTOP_PAGE_SIZE } from '../../../config/links/links';
import { MAX_CATEGORY_DEALS_BLOCK } from '../../../config/setup/setup';
import { cutArray } from '../../../helpers/deals';
import { isWowcherIE } from '../../../helpers/pages';
import { isSSR } from '../../../helpers/ssr';
import { parseWowcherPath } from '../../../helpers/url';
import useClientSideEffect from '../../../helpers/useClientSideEffect';
import useRestoreScroll from '../../../helpers/useRestoreScroll';
import { useVideoScroll } from '../../../helpers/video';
import ThemeContext from '../../../providers/ThemeProvider';
import AdditionalContent from '../../AdditionalContent';
import VipContact from '../../VipContact';
import TwoByTwoPlaceholder from '../../_generic/loading-placeholder/TwoByTwoPlaceholder';
import Spinner from '../../_generic/spinner/Spinner';
import MerchandisingModule from '../../category/MerchandisingModule';
import PopularLocationsList from '../../popular-locations-list/PopularLocationsList';
import TwoByTwoDealCard from '../TwoByTwoDealCard';
import BlockWithOneFeaturedDeal from '../shared-components/blockWithOneFeaturedDeal';
import EndOfFeed from './EndOfFeed';
import LoadMore from './LoadMore';
import LoadPrevious from './LoadPrevious';
import { DEAL_FOCUS_RETRIES } from './helpers';

export const LAYOUT_TYPES = {
  claimTwoByTwo: 'claim-2x2',
  grid: 'grid',
  twoByTwo: '2x2',
};
export const filterTemplate = ({
  type,
  processedItems,
  path,
  popularLocationsData,
  theme,
  isVipHubPage,
  isEarlyBird,
  additionalContentText,
  isWowcherIe = false,
  isNewFilterApplied,
  secondaryDealsToExclude,
  hasQueryParams,
}) => {
  // Common filtering function for both layouts
  const filterSecondaryDeals = (deals) => {
    if (!secondaryDealsToExclude || secondaryDealsToExclude.length === 0) {
      return deals;
    }

    return deals.filter(
      (item) =>
        !secondaryDealsToExclude.some(
          (excludedDeal) => excludedDeal.urlPath === item.urlPath,
        ),
    );
  };

  switch (type) {
    case LAYOUT_TYPES.claimTwoByTwo:
    case LAYOUT_TYPES.twoByTwo: {
      const flatItems = processedItems?.flat()?.map((item, index) => {
        return {
          ...item,
          order: index % 2 === 0 ? ORDER.even : ORDER.odd,
        };
      });

      const sectionedItems = cutArray(flatItems, 8);

      return sectionedItems.map((page, pageIndex) => {
        return (
          <Fragment key={pageIndex}>
            <div className="main-container-static">
              {isEarlyBird && (
                <img
                  alt="background-black"
                  className="bg-container"
                  src={VIP_BG}
                />
              )}
              <div
                className={classNames(
                  'page bottom-deals container deals-products-list deal-block',
                  {
                    'with-filter-layout': isNewFilterApplied,
                  },
                )}
              >
                {page.map((item) => (
                  <TwoByTwoDealCard
                    deal={item}
                    key={`${item.urlPath}${item.isSponsored && '-sponsored'}`}
                  />
                ))}
              </div>
              <style jsx>{`
                .page.container {
                  display: flex;
                  flex-wrap: wrap;
                  flex-direction: row;
                  justify-content: space-between;
                  align-items: stretch;
                  padding-top: 15px;
                  margin-bottom: -20px;
                }
                .bg-container {
                  position: fixed;
                  height: 100vh;
                  width: 100vw;
                  object-fit: cover;
                  z-index: -1;
                  margin-top: -14rem;
                }
                @media (max-width: ${theme.breakpoints.smUp}) {
                  .main-container-static {
                  }
                }
                @media (max-width: ${theme.breakpoints.smDown}) {
                  .page.container {
                    margin-top: 0;
                    margin-bottom: 0;
                    padding: 0px;
                  }
                }
              `}</style>
              {!hasQueryParams && (
                <MerchandisingModule
                  isNewFilterApplied={isNewFilterApplied}
                  path={path}
                  position={pageIndex + 1}
                />
              )}
              {pageIndex === 0 && additionalContentText && !hasQueryParams && (
                <AdditionalContent
                  additionalContentText={additionalContentText}
                  isNewFilterApplied={isNewFilterApplied}
                />
              )}
            </div>
          </Fragment>
        );
      });
    }

    case LAYOUT_TYPES.grid:
    default: {
      const filteredDeals = filterSecondaryDeals(processedItems?.flat());
      const flatDeals = filteredDeals?.map((deal, index) => {
        return { ...deal, order: index % 2 === 0 ? ORDER.even : ORDER.odd };
      });
      const sectionedDeals = cutArray(flatDeals, MAX_CATEGORY_DEALS_BLOCK);

      return sectionedDeals.map((page, index) => {
        return (
          <Fragment key={index}>
            <div
              className={classNames(
                'bottom-deals container deals-products-list deal-block',
                {
                  'with-filter-layout': isNewFilterApplied,
                },
              )}
            >
              <BlockWithOneFeaturedDeal
                deals={page}
                isNewFilterApplied={isNewFilterApplied}
                position={index}
              />
              {index === 0 && !isWowcherIe && (
                <PopularLocationsList
                  popularLocationsData={popularLocationsData}
                />
              )}
            </div>

            {index === 0 && path === `/deals/vip` && <VipContact key={index} />}
            {!isVipHubPage && !hasQueryParams && (
              <MerchandisingModule
                isNewFilterApplied={isNewFilterApplied}
                path={path}
                position={index + 1}
              />
            )}
            {index === 0 && additionalContentText && !hasQueryParams && (
              <AdditionalContent
                additionalContentText={additionalContentText}
                isNewFilterApplied={isNewFilterApplied}
              />
            )}
          </Fragment>
        );
      });
    }
  }
};

export const template = ({
  type,
  deals,
  path,
  popularLocationsData,
  theme,
  ppNotVisible = false,
  isVipHubPage,
  isNewFilterApplied,
  isEarlyBird,
  additionalContentText,
  isWowcherIe = false,
  secondaryDealsArray,
  isSubCat = false,
  isCategoryPage = false,
  canRemoveSecondary = false,
}) => {
  switch (type) {
    case LAYOUT_TYPES.claimTwoByTwo:
    case LAYOUT_TYPES.twoByTwo: {
      const flatDeals = deals.flat().map((deal, index) => {
        return { ...deal, order: index % 2 === 0 ? ORDER.even : ORDER.odd };
      });
      const sectionedDeals = cutArray(flatDeals, 8);
      if (
        (isSubCat || isCategoryPage) &&
        sectionedDeals?.[0]?.length &&
        secondaryDealsArray?.length &&
        canRemoveSecondary
      ) {
        const excludeIds = new Set(secondaryDealsArray.map((s) => s.id));
        sectionedDeals[0] = sectionedDeals[0].filter(
          (deal) => !excludeIds.has(deal.id),
        );
      }

      return sectionedDeals.map((page, index) => {
        return (
          <Fragment key={index}>
            <div className="main-container-static">
              {isEarlyBird && (
                <img
                  alt="background-black"
                  className="bg-container"
                  src={VIP_BG}
                />
              )}
              <div
                className={classNames(
                  'page bottom-deals container deals-products-list deal-block',
                  {
                    'with-filter-layout': isNewFilterApplied,
                  },
                )}
              >
                {page.map((deal) => {
                  const isClaimLayout = type === LAYOUT_TYPES.claimTwoByTwo;
                  if (isClaimLayout) deal.isClaim = true;

                  return (
                    <TwoByTwoDealCard
                      deal={deal}
                      isHideSplat={isClaimLayout}
                      key={`${deal.id}${deal.isSponsored && '-sponsored'}`}
                      // sponsored deals can be repeated in the feed so make sure keys are different
                      ppNotVisible={ppNotVisible}
                    />
                  );
                })}
              </div>
              <style jsx>{`
                .page.container {
                  display: flex;
                  flex-wrap: wrap;
                  flex-direction: row;
                  justify-content: space-between;
                  align-items: stretch;
                  padding-top: 20px;
                  margin-bottom: -20px;
                }

                .bg-container {
                  position: fixed;
                  height: 100vh;
                  width: 100vw;
                  object-fit: cover;
                  z-index: -1;
                  margin-top: -14rem;
                }
                @media (max-width: ${theme.breakpoints.smUp}) {
                  .main-container-static {
                  }
                }
                @media (max-width: ${theme.breakpoints.smDown}) {
                  .page.container {
                    margin-top: 0;
                    margin-bottom: 0;
                    padding: 0px;
                  }
                }
              `}</style>
              <MerchandisingModule
                isNewFilterApplied={isNewFilterApplied}
                path={path}
                position={index + 1}
              />
              {index === 0 && additionalContentText && (
                <AdditionalContent
                  additionalContentText={additionalContentText}
                  isNewFilterApplied={isNewFilterApplied}
                />
              )}
            </div>
          </Fragment>
        );
      });
    }
    case LAYOUT_TYPES.grid:
    default: {
      const flatDeals = deals.flat().map((deal, index) => {
        return { ...deal, order: index % 2 === 0 ? ORDER.even : ORDER.odd };
      });
      const sectionedDeals = cutArray(flatDeals, MAX_CATEGORY_DEALS_BLOCK);
      if (
        (isSubCat || isCategoryPage) &&
        sectionedDeals?.[0]?.length &&
        secondaryDealsArray?.length &&
        canRemoveSecondary
      ) {
        const excludeIds = new Set(secondaryDealsArray.map((s) => s.id));
        sectionedDeals[0] = sectionedDeals[0].filter(
          (deal) => !excludeIds.has(deal.id),
        );
      }

      return sectionedDeals.map((page, index) => {
        return (
          <Fragment key={index}>
            <div
              className={classNames(
                'bottom-deals container deals-products-list',
                {
                  'with-filter-layout': isNewFilterApplied,
                },
              )}
            >
              <BlockWithOneFeaturedDeal
                deals={page}
                isNewFilterApplied={isNewFilterApplied}
                position={index}
              />
              {index === 0 && !isWowcherIe && (
                <PopularLocationsList
                  popularLocationsData={popularLocationsData}
                />
              )}
            </div>

            {index === 0 && path === `/deals/vip` && <VipContact key={index} />}
            {!isVipHubPage && (
              <MerchandisingModule
                isNewFilterApplied={isNewFilterApplied}
                path={path}
                position={index + 1}
              />
            )}
            {index === 0 && additionalContentText && (
              <AdditionalContent
                additionalContentText={additionalContentText}
                isNewFilterApplied={isNewFilterApplied}
              />
            )}
          </Fragment>
        );
      });
    }
  }
};

export const placeholderTemplate = (templateType, newFilter = false) => {
  switch (templateType) {
    case LAYOUT_TYPES.twoByTwo: {
      return (
        <>
          <div className={newFilter ? 'with-margin' : ''}>
            <TwoByTwoPlaceholder />
            <TwoByTwoPlaceholder />
          </div>
          <style jsx>{`
            .with-margin {
              margin-left: 15px;
            }
          `}</style>
        </>
      );
    }
    case LAYOUT_TYPES.grid:
    default: {
      return (
        <>
          <div className={newFilter ? 'spinner-container' : ''}>
            <Spinner display="relative" msgText="Loading..." />
          </div>
          <style jsx>{`
            .spinner-container {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 890px;
            }
          `}</style>
        </>
      );
    }
  }
};
const ConnectedBottomDeals = ({
  additionalContentText,
  chosenFilters,
  emptyText = '',
  fetcherFunction,
  firstPageDeals,
  firstPageSize,
  getKeyGenerator,
  isGuidepage,
  isSearchPage = false,
  numberSecondaryDeals,
  pageSize = DESKTOP_PAGE_SIZE,
  popularLocationsData = [],
  sponsoredSearchDeals = [],
  isNewFilterApplied = false,
  secondaryDealsToExclude,
  templateType,
  totalDeals,
  onFiltersUpdate,
  setResultCount,
  newFilter = false,
  secondaryDealsArray,
  isSubCat = false,
  isCategoryPage = false,
  feedConfig,
}) => {
  const theme = useContext(ThemeContext);
  const router = useRouter();
  const scrollRetriesRef = useRef(0);
  const [restoreScrollData, setRestoreScrollData] = useRestoreScroll();
  const [canRemoveSecondary, setCanRemoveSecondary] = useState(true);
  const [startPageNr, setStartPageNr] = useState(
    !isSSR() && restoreScrollData?.url === window.location.href
      ? restoreScrollData?.page
      : 0,
  );
  const { searchParams } = parseWowcherPath(router.asPath);
  const query = searchParams.get('q') || '';
  const getKey = useMemo(() => {
    return getKeyGenerator(startPageNr);
  }, [getKeyGenerator, startPageNr, query]); // Add query as dependency
  const componentRef = useRef(null);
  const dispatch = useDispatch();
  const isWowcherIe = isWowcherIE();
  const path = router.asPath;
  const hasQueryParameters = router.asPath.includes('?');
  const pathUrl = parseWowcherPath(path);
  const isVipHubPage = VIP_QUALIFIERS.includes(pathUrl.pageType);

  // Add this ref to track the last set of items
  const lastItemsRef = useRef(null);
  const firstPageDealsRef = useRef(firstPageDeals);

  // Update ref when firstPageDeals changes
  useEffect(() => {
    firstPageDealsRef.current = firstPageDeals;
  }, [firstPageDeals]);

  const stableFetcher = useRef((url) => {
    if (isSearchPage) {
      return fetcherFunction(url, firstPageDealsRef.current);
    }

    return fetcherFunction(url);
  }).current;

  let { data: items, error, setSize, isValidating, size } = useSWRInfinite(
    getKey,
    stableFetcher,
    {
      dedupingInterval: 600_000,
      initialSize: 1,
      revalidateOnFocus: false,
    },
  );
  let processedItems = useMemo(() => {
    if (!items || !firstPageDeals || firstPageDeals.length === 0) {
      return items;
    }

    // Only override if we're on the actual first page (startPageNr === 0)
    if (startPageNr !== 0) {
      return items;
    }

    // Check if we need to override the first page
    const currentFirstPage = items[0];
    const shouldOverride =
      currentFirstPage &&
      firstPageDeals &&
      currentFirstPage.length > 0 &&
      firstPageDeals.length > 0 &&
      currentFirstPage[0]?.id !== firstPageDeals[0]?.id;

    if (shouldOverride) {
      console.log('Overriding stale first page data with fresh firstPageDeals');

      return [firstPageDeals, ...items.slice(1)];
    }

    return items;
  }, [items, firstPageDeals, query, startPageNr]);

  const isEmpty = processedItems?.[0]?.length === 0;

  useEffect(() => {
    if (isNewFilterApplied && processedItems) {
      const pageWithFilters = processedItems.find(
        (p) =>
          p?.facetedNavigation?.filters &&
          Object.keys(p.facetedNavigation.filters).length > 0,
      )?.facetedNavigation;

      if (
        pageWithFilters?.filters &&
        onFiltersUpdate &&
        Object.keys(pageWithFilters.filters).length > 0
      ) {
        onFiltersUpdate(pageWithFilters.filters);
        setResultCount(pageWithFilters.count);
      }
    }
  }, [processedItems]);

  // Handle sponsored search items
  if (sponsoredSearchDeals.length > 0 && processedItems?.length > 0) {
    processedItems = [...sponsoredSearchDeals, ...processedItems];
  }

  // These MUST be calculated using the original feed (before dupes removed)
  // const isEmpty = items?.[0]?.length === 0;
  const isLoadingInitialData = !items && !error;

  // remove secondary deals from top of page 0
  if (
    processedItems &&
    canRemoveSecondary &&
    numberSecondaryDeals &&
    firstPageSize === processedItems?.[0]?.length
  ) {
    processedItems?.[0]?.splice(0, numberSecondaryDeals);
    setCanRemoveSecondary(false);
  }

  const pageLimitForGuidePage =
    isGuidepage && processedItems?.flat().length > totalDeals;

  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 &&
      processedItems &&
      typeof processedItems[size - 1] === 'undefined');
  const actualPageSize = isSearchPage ? pageSize - 6 : pageSize;

  // Check if dataSearchQuery is present in feedConfig
  const hasDataSearchQuery = feedConfig?.dataSearchQuery;

  const isReachingEnd = hasDataSearchQuery
    ? isEmpty ||
      (processedItems &&
        processedItems[processedItems.length - 1]?.length === 0) ||
      pageLimitForGuidePage
    : isEmpty ||
      (processedItems &&
        processedItems[processedItems.length - 1]?.length < actualPageSize) ||
      pageLimitForGuidePage;
  useVideoScroll(processedItems);

  // Remove or update the problematic useClientSideEffect
  useClientSideEffect(() => {
    // When startPageNr changes, we need to reset the data
    if (isSearchPage) {
      setSize(1); // Reset to first page to trigger fresh data with new startPageNr
    }
  }, [startPageNr]);

  // Add effect to handle search query changes
  useEffect(() => {
    // Reset everything when search query changes
    if (isSearchPage) {
      setStartPageNr(0);
      setSize(1);
      setCanRemoveSecondary(true);
    }
  }, [query]);

  useEffect(() => {
    // clear start page when user changes filters to avoid showing empty pages
    setStartPageNr(0);
  }, [router.query]);

  useEffect(() => {
    if (processedItems) {
      const flatItems = processedItems.flat();
      dispatch({ deals: flatItems, type: TRACKING.SET_TRACKING_DEALS });
      // Store the current items in the ref
      lastItemsRef.current = JSON.stringify(
        flatItems.map((item) => item?.id || item?.productId),
      );
    }
  }, [processedItems]);

  useClientSideEffect(() => {
    const handleComplete = () => {
      const dealCardElement = document.querySelector(
        `#deal_${restoreScrollData.dealId}`,
      );
      if (dealCardElement) {
        if (typeof dealCardElement.scrollIntoViewIfNeeded === 'undefined') {
          dealCardElement.scrollIntoView({
            block: 'center',
            inline: 'center',
          });
        } else {
          dealCardElement.scrollIntoViewIfNeeded();
        }

        setRestoreScrollData(null);
        scrollRetriesRef.current = 0;
      } else if (scrollRetriesRef.current <= DEAL_FOCUS_RETRIES.length) {
        ++scrollRetriesRef.current;
        setTimeout(
          handleComplete,
          DEAL_FOCUS_RETRIES[scrollRetriesRef.current],
        );
      } else {
        // Reset
        scrollRetriesRef.current = 0;
      }
    };

    if (restoreScrollData?.url === window.location.href) {
      if (restoreScrollData.page !== startPageNr)
        setStartPageNr(restoreScrollData.page);
      if (!isValidating) handleComplete();
    }
  }, [
    router.asPath,
    restoreScrollData,
    setRestoreScrollData,
    isValidating,
    startPageNr,
    setStartPageNr,
  ]);

  const handleLoadPrevious = () => {
    if (componentRef?.current) {
      const containerHeight = componentRef.current.getBoundingClientRect()
        .height;
      if (componentRef.current.style) {
        componentRef.current.style.minHeight = `${containerHeight}px`;
      }
    }
    setStartPageNr(startPageNr - 1);
  };

  useClientSideEffect(() => {
    if (!isValidating && componentRef.current?.style.minHeight) {
      const dealBlock = componentRef.current?.querySelector('.deal-block');
      if (dealBlock) {
        const height = dealBlock.getBoundingClientRect().height;
        window.scrollBy(0, height * 2);
      }
      componentRef.current.style.minHeight = '';
    }
  }, [isValidating]);

  const handleLoadMore = () => {
    if (!isLoadingMore && !isValidating) {
      setSize(size + 1);
    }
  };

  const renderDeals = () => {
    if (!processedItems?.length) return null;
    if (pageLimitForGuidePage) {
      processedItems = processedItems.flat().slice(0, totalDeals);
    }

    let dealsTemplate;

    if (isNewFilterApplied) {
      dealsTemplate = filterTemplate({
        additionalContentText,
        hasQueryParams: hasQueryParameters,
        isEarlyBird: false,
        isNewFilterApplied,
        isVipHubPage,
        isWowcherIe,
        path: router.asPath,
        popularLocationsData,
        processedItems,
        secondaryDealsToExclude,
        theme,
        type: templateType,
      });
    } else {
      dealsTemplate = template({
        additionalContentText,
        canRemoveSecondary,
        deals: processedItems,
        isCategoryPage,
        isGuidepage,
        isNewFilterApplied,
        isSubCat,
        isVipHubPage,
        isWowcherIe,
        path: router.asPath,
        popularLocationsData,
        secondaryDealsArray,
        theme,
        type: templateType,
      });
    }

    return (
      <div>
        {startPageNr > 0 && <LoadPrevious onClick={handleLoadPrevious} />}
        {dealsTemplate}
        {items?.length > 0 && !isLoadingMore && !isReachingEnd && (
          <LoadMore onTrigger={handleLoadMore} />
        )}
      </div>
    );
  };

  if (isEmpty) {
    return emptyText ? (
      <div className="container">
        <p className="empty-text">{emptyText}</p>
        <style jsx>{`
          .empty-text {
            text-align: center;
            font-size: 16px;
            font-weight: bold;
            color: #888;
            padding: 40px 20px;
          }
        `}</style>
      </div>
    ) : null;
  }

  return (
    <div ref={componentRef}>
      {renderDeals()}
      {isLoadingMore && placeholderTemplate(templateType, newFilter)}
      <EndOfFeed isNewFilterApplied={isNewFilterApplied} show={isReachingEnd} />
      <style global jsx>{`
        .container-wrapper .container .bottom-deals-block.sequence-blocks .medium-deal {
          margin-top: 20px;
        }
        .with-filter-layout {
          width: 900px !important; 
          margin: 0px 0px;
        }
        .with-filter-layout .bottom-deals-block {
          width: 100% !important; 
        }
        .bottom-deals.deals-products-list .bottom-deals-block .main-deal {
          margin-bottom: 0px !important;
        }
        .bottom-deals.deals-products-list .sequence-of-3 {
          margin-top: 0px !important;
        }
        .ipad-row .medium-deal:first-child {
          margin-top: 20px;
        }
        .bottom-deals.deals-products-list .medium-deals-block {
          width: 100%;
        }
        .bottom-deals.deals-products-list .sequence-of-3 {
          margin-top: 20px;
          width: 100%;
        }
        .bottom-deals.deals-products-list .full-row {
          width: 100%;
          margin-bottom: 20px;
        }
        .bottom-deals.deals-products-list .row-display {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
        .bottom-deals.deals-products-list .cols-display {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
        }
        .bottom-deals.deals-products-list .cols {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
        .bottom-deals.deals-products-list .bottom-deals-block.sequence-blocks .item {
          margin-bottom: 20px;
        }
        .bottom-deals.deals-products-list .nowrap {
          flex-wrap: nowrap;
        }
        @media (max-width: ${theme.breakpoints.xlDown}) {
          .ipad-row .medium-deal:last-child {
            margin-top: 20px;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .main-deal {
            padding-right: 0px;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .main-deal {
            width: 100%;
            margin-bottom: 0px;
            //margin-bottom: 20px;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals { width: 100%; height: auto;}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row {
            flex-direction: row;
            margin-bottom: 0px;
            width: 666px;
            margin: 0 auto;
          }
          .bottom-deals.deals-products-list .bottom-deals-block.sequence-blocks {
            width: 666px !important;
            margin: 0 auto;
          }
          .bottom-deals.deals-products-list .sequence-of-3 {
            margin-bottom: 0px;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal { width: 50%}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal { width: 50% !important; height: 250px;}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal :nth-child(even) {margin-left: 10px;}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal :nth-child(odd) {margin-right: 10px;}
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .bottom-deals.deals-products-list .container {
            padding: 0px;
          }
          .bottom-deals.deals-products-list .row {
            padding-left: 0px;
            padding-right: 0px;
            margin-left: 0px;
            margin-right: 0px;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .item { height: 300px; !important}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal { width: 100% !important; height: 300px !important}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row { flex-direction: column; }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row {
            width: 566px !important;
            flex-direction: row;
            flex-wrap: wrap;
          }
          .bottom-deals.deals-products-list .bottom-deals-block.sequence-blocks {
            width: 566px !important;
          }
          .bottom-deals.deals-products-list .bottom-deals-block.sequence-blocks  .bottom-deal-item  {width: 100%;}
          .bottom-deals.deals-products-list .bottom-deals-block  .bottom-deal-item { width: 100%; }
          .bottom-deals.deals-products-list .bottom-deals-block .item { height: auto;}
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .deal:first-child { margin-bottom: 20px;}
          .bottom-deals-block.sequence-blocks {
            width: 566px;
          }
          .empty-medium-deal2 {
            display: none !important;
          }
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .bottom-deals.deals-products-list .bottom-deals-block.sequence-blocks {
            width: 100% !important;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row {
            width: 100% !important;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row .medium-deal{
            margin-top: 20px;
          }
          .cols-display .ipad-row,
          .bottom-deals-block .side-deals .ipad-row {
            width: 100% !important;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row {
            margin: 0;
          }
          .empty-medium-deal {
            display: none !important;
          }
          .bottom-deals.deals-products-list .bottom-deals-block .side-deals .ipad-row .medium-deal,
          .container-wrapper .container .bottom-deals-block.sequence-blocks .medium-deal {
            margin-top: 5px;
          }
        }
      `}</style>
    </div>
  );
};

ConnectedBottomDeals.propTypes = {
  additionalContentText: PropTypes.string,
  chosenFilters: PropTypes.array,
  emptyText: PropTypes.any,
  fetcherFunction: PropTypes.func.isRequired,
  firstPageDeals: PropTypes.array,
  firstPageSize: PropTypes.number,
  getKeyGenerator: PropTypes.func.isRequired,
  isGuidepage: PropTypes.bool,
  isNewFilterApplied: PropTypes.bool,
  isSearchPage: PropTypes.bool,
  numberSecondaryDeals: PropTypes.number,
  pageSize: PropTypes.number,
  popularLocationsData: PropTypes.array,
  setResultCount: PropTypes.func,
  sponsoredSearchDeals: PropTypes.array,
  templateType: PropTypes.string,
  totalDeals: PropTypes.number,
};

export default ConnectedBottomDeals;
